import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { theme } from '../../styles/theme';
import { useIsMobile } from '../../Utils/useIsMobile';
import { convertToVwMob } from '../../Utils/responsive';
import chevronBackIcon from '../../assets/Icon/project/chvoron_back.png';
import dropdownIcon from '../../assets/Icon/project/inputbox_icon.png';

const WEEKDAYS = ['일', '월', '화', '수', '목', '금', '토'];
const MONTHS = Array.from({ length: 12 }, (_, i) => `${i + 1}월`);
const MOBILE_PICKER_ITEM_HEIGHT = 46;
const MOBILE_MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_MIN = 1900;
const YEAR_MAX = CURRENT_YEAR + 2;
const YEARS = Array.from({ length: YEAR_MAX - YEAR_MIN + 1 }, (_, i) => YEAR_MIN + i);

function parseDate(value: string): { year: number; month: number; day: number } | null {
  if (!value || !/^\d{4}-\d{2}-\d{2}$/.test(value)) return null;
  const [y, m, d] = value.split('-').map(Number);
  if (y < YEAR_MIN || y > YEAR_MAX || m < 1 || m > 12 || d < 1 || d > 31) return null;
  const date = new Date(y, m - 1, d);
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null;
  return { year: y, month: m, day: d };
}

function formatDate(year: number, month: number, day: number): string {
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function getCalendarDays(year: number, month: number, selectedDay: number | null) {
  const firstDay = new Date(year, month - 1, 1);
  const daysInMonth = new Date(year, month, 0).getDate();
  const startOffset = firstDay.getDay();
  const prevMonthDays = new Date(year, month - 1, 0).getDate();
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7;
  
  const days: { date: number; isCurrentMonth: boolean; isSelected: boolean }[] = [];
  
  for (let i = 0; i < totalCells; i++) {
    if (i < startOffset) {
      days.push({ date: prevMonthDays - startOffset + i + 1, isCurrentMonth: false, isSelected: false });
    } else if (i < startOffset + daysInMonth) {
      const date = i - startOffset + 1;
      days.push({ date, isCurrentMonth: true, isSelected: selectedDay === date });
    } else {
      days.push({ date: i - startOffset - daysInMonth + 1, isCurrentMonth: false, isSelected: false });
    }
  }
  
  return days;
}

// --- Styled ---
const Overlay = styled.div<{ $variant: 'modal' | 'sheet' }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2000;
  display: flex;
  align-items: ${({ $variant }) => ($variant === 'sheet' ? 'flex-end' : 'center')};
  justify-content: center;
  padding: ${({ $variant }) => ($variant === 'sheet' ? '0' : '1rem')};
`;

const Modal = styled.div<{ $variant: 'modal' | 'sheet' }>`
  background: #F8FDFF;
  border-radius: 1rem;
  padding-top: 23px;
  padding-right: 28.5px;
  padding-bottom: 18px;
  padding-left: 28.5px;
  width: min(360px, 95vw);
  height: min(460px, 85vh);
  max-width: 95vw;
  max-height: 85vh;
  box-sizing: border-box;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  margin: 0 auto;
  ${({ $variant }) =>
    $variant === 'sheet' &&
    css`
      width: 100%;
      max-width: 100%;
      height: auto;
      border-radius: 24px 24px 0 0;
      margin: 0;
      box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
      background: #ffffff;
      padding: ${convertToVwMob(30)} ${convertToVwMob(20)} calc(${convertToVwMob(40)} + env(safe-area-inset-bottom));
      overflow-y: auto;
    `}
`;

const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${convertToVwMob(18)};
`;

const MobileTitle = styled.div`
  ${theme.typography.head.semiBold20};
  color: ${theme.colors.grayScale[90]};
  margin: 0;
  font-size: 24px;
  margin-bottom: ${convertToVwMob(18)};
`;

const MobileCloseButton = styled.button`
  width: ${convertToVwMob(20)};
  height: ${convertToVwMob(20)};
  background: none;
  border: none;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 20 20' fill='none'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M19.7011 1.74203C19.8925 1.55065 20 1.2911 20 1.02045C20 0.749813 19.8925 0.490257 19.7011 0.298884C19.5097 0.107512 19.2502 0 18.9795 0C18.7089 0 18.4493 0.107512 18.258 0.298884L10 8.55686L1.74203 0.298884C1.55065 0.107512 1.2911 1.97053e-08 1.02045 2.17217e-08C0.749813 2.37381e-08 0.490257 0.107512 0.298884 0.298884C0.107512 0.490257 2.37381e-08 0.749813 2.17217e-08 1.02045C1.97053e-08 1.2911 0.107512 1.55065 0.298884 1.74203L8.55686 10L0.298884 18.258C0.107512 18.4493 0 18.7089 0 18.9795C0 19.2502 0.107512 19.5097 0.298884 19.7011C0.490257 19.8925 0.749813 20 1.02045 20C1.2911 20 1.55065 19.8925 1.74203 19.7011L10 11.4431L18.258 19.7011C18.4493 19.8925 18.7089 20 18.9795 20C19.2502 20 19.5097 19.8925 19.7011 19.7011C19.8925 19.5097 20 19.2502 20 18.9795C20 18.7089 19.8925 18.4493 19.7011 18.258L11.4431 10L19.7011 1.74203Z' fill='%23676767'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
  background-size: contain;
  cursor: pointer;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.25rem;
  gap: 0.5rem;
  width: 100%;
`;

const MobilePickerContent = styled.div`
  height: ${convertToVwMob(242)};
  position: relative;
  overflow: hidden;
`;

const MobilePickerScrollArea = styled.div`
  display: flex;
  height: 100%;
  gap: ${convertToVwMob(20)};
  position: relative;
`;

const MobilePickerColumn = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${convertToVwMob(98)} 0;
  scroll-snap-type: y mandatory;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const MobilePickerItem = styled.div<{ $selected?: boolean }>`
  height: ${convertToVwMob(MOBILE_PICKER_ITEM_HEIGHT)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${convertToVwMob(24)};
  color: ${({ $selected }) => ($selected ? theme.colors.grayScale[80] : theme.colors.grayScale[40])};
  font-weight: 400;
  cursor: pointer;
  width: calc(100% - ${convertToVwMob(20)});
  margin: 0 auto;
  background: ${({ $selected }) => ($selected ? theme.colors.grayScale[20] : 'transparent')};
  border-radius: 12px;
  scroll-snap-align: center;
  flex-shrink: 0;
`;

const MobileCalendarHeader = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: ${convertToVwMob(20)};
`;

const MobileNavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(12)};
`;

const MobileNavButton = styled.button<{ $right?: boolean }>`
  width: ${convertToVwMob(24)};
  height: ${convertToVwMob(24)};
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    transform: ${(p) => (p.$right ? 'scaleX(-1)' : 'none')};
  }
`;

const MobileNavLabel = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  gap: ${convertToVwMob(6)};
  font-size: ${convertToVwMob(24)};
  font-weight: 600;
  line-height: 150%;
  color: ${theme.colors.grayScale[90]};
`;

const MobileDropdownIcon = styled.img`
  width: ${convertToVwMob(12)};
  height: ${convertToVwMob(12)};
  object-fit: contain;
`;

const NavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const NavButton = styled.button<{ $right?: boolean }>`
  width: 2rem;
  height: 2rem;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  &:hover {
    background: ${theme.colors.grayScale[20]};
  }
  img {
    width: 1rem;
    height: 1rem;
    object-fit: contain;
    transform: ${(p) => (p.$right ? 'scaleX(-1)' : 'none')};
  }
`;

const DropdownIcon = styled.img`
  width: 0.75rem;
  height: 0.75rem;
  object-fit: contain;
  margin-left: 0.125rem;
`;

const NavLabel = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0.25rem 0.5rem;
  ${theme.typography.chip.medium16};
  color: ${theme.colors.grayScale[90]};
  display: flex;
  align-items: center;
  gap: 0.25rem;
  border-radius: 0.25rem;
  &:hover {
    background: ${theme.colors.grayScale[20]};
  }
`;

const WeekdayRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 0 6.5px;
  margin-bottom: 24px;
`;

const WeekdayCell = styled.div`
  text-align: center;
  ${theme.typography.caption.regular14};
  color: ${theme.colors.grayScale[70]};
  padding: 0;
  width: 100%;
  min-width: 0;
`;

const CalendarContent = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 24px 6.5px;
  flex: 1;
  min-height: 0;
  width: 100%;
  align-content: start;
`;

const DayCell = styled.button<{ $dimmed?: boolean; $selected?: boolean }>`
  aspect-ratio: 1;
  border: none;
  padding: 0;
  background: ${(p) => (p.$selected ? theme.colors.primaryBlue[40] : 'transparent')};
  color: ${(p) => (p.$selected ? 'white' : p.$dimmed ? theme.colors.grayScale[50] : theme.colors.grayScale[90])};
  ${theme.typography.caption.regular14};
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  width: 100%;
  min-width: 0;
  &:hover:not(:disabled) {
    background: #2BC2E1;
    color: white;
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  margin-top: 1.25rem;
`;

const FooterBtn = styled.button`
  padding: 0.5rem 1rem;
  border: none;
  background: transparent;
  cursor: pointer;
  ${theme.typography.caption.m3};
  color: ${theme.colors.primaryBlue[40]};
  &:hover {
    opacity: 0.9;
  }
`;

const MobileConfirmButton = styled.button`
  width: 100%;
  height: ${convertToVwMob(62)};
  margin-top: ${convertToVwMob(24)};
  background: ${theme.colors.primaryBlue[80]};
  color: white;
  border: none;
  border-radius: 12px;
  ${theme.typography.button.medium20};
  cursor: pointer;
`;

const DropdownWrap = styled.div`
  margin-left: -28.5px;
  margin-right: -28.5px;
  width: calc(100% + 57px);
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
`;

const DropdownHeader = styled.div`
  display: flex;
  width: 100%;
  min-width: 0;
  min-height: 65px;
  padding: 22px 54px 22px 56px;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[70]};
  border-bottom: 1px solid ${theme.colors.grayScale[20]};
  margin-bottom: 0;
  flex-shrink: 0;
`;

const DropdownHeaderPart = styled.button`
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[70]};
  &:hover {
    color: ${theme.colors.grayScale[90]};
  }
`;

const DropdownList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  width: 100%;
`;

const DropdownItem = styled.div<{ $selected?: boolean }>`
  width: 100%;
  padding: 12px 28.5px 12px 20px;
  box-sizing: border-box;
  ${theme.typography.contents.regular16};
  color: ${theme.colors.grayScale[90]};
  background: ${(p) => (p.$selected ? theme.colors.primaryBlue[15] : 'transparent')};
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  min-height: 44px;
  &:hover {
    background: ${theme.colors.grayScale[10]};
  }
`;

const CheckSlot = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.25rem;
  flex-shrink: 0;
`;

const CheckIcon = styled.span`
  color: #404040;
  font-weight: bold;
`;

const DropdownLabelGroup = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

export interface BirthDatePickerProps {
  isOpen: boolean;
  value: string;
  onConfirm: (value: string) => void;
  onCancel: () => void;
}

export function BirthDatePicker({ isOpen, value, onConfirm, onCancel }: BirthDatePickerProps) {
  const isMobile = useIsMobile();
  const [year, setYear] = useState(() => {
    const parsed = parseDate(value);
    return parsed?.year ?? new Date().getFullYear();
  });
  const [month, setMonth] = useState(() => {
    const parsed = parseDate(value);
    return parsed?.month ?? new Date().getMonth() + 1;
  });
  const [day, setDay] = useState<number | null>(() => {
    const parsed = parseDate(value);
    return parsed?.day ?? null;
  });
  const [view, setView] = useState<'calendar' | 'month' | 'year'>('calendar');
  const monthListRef = useRef<HTMLDivElement>(null);
  const yearListRef = useRef<HTMLDivElement>(null);
  const mobileYearRef = useRef<HTMLDivElement>(null);
  const mobileMonthRef = useRef<HTMLDivElement>(null);
  const [tempYear, setTempYear] = useState(() => {
    const parsed = parseDate(value);
    return parsed?.year ?? new Date().getFullYear();
  });
  const [tempMonth, setTempMonth] = useState(() => {
    const parsed = parseDate(value);
    return parsed?.month ?? new Date().getMonth() + 1;
  });

  useEffect(() => {
    if (!isOpen) return;
    const parsed = parseDate(value);
    const now = new Date();
    const nextYear = parsed?.year ?? now.getFullYear();
    const nextMonth = parsed?.month ?? now.getMonth() + 1;
    setYear(nextYear);
    setMonth(nextMonth);
    setDay(parsed?.day ?? null);
    setTempYear(nextYear);
    setTempMonth(nextMonth);
    setView('calendar');
  }, [isOpen, value]);

  useEffect(() => {
    if (view === 'month' && monthListRef.current) {
      const el = monthListRef.current.querySelector(`[data-month="${month}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    } else if (view === 'year' && yearListRef.current) {
      const el = yearListRef.current.querySelector(`[data-year="${year}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [view, month, year]);

  useEffect(() => {
    if (!isMobile || view === 'calendar') return;
    setTempYear(year);
    setTempMonth(month);
    if (typeof window === 'undefined') return;
    setTimeout(() => {
      const itemHeightPx = (window.innerWidth * MOBILE_PICKER_ITEM_HEIGHT) / 393;
      const yearIndex = YEARS.indexOf(year);
      const monthIndex = MOBILE_MONTHS.indexOf(month);
      if (mobileYearRef.current && yearIndex >= 0) {
        mobileYearRef.current.scrollTop = yearIndex * itemHeightPx;
      }
      if (mobileMonthRef.current && monthIndex >= 0) {
        mobileMonthRef.current.scrollTop = monthIndex * itemHeightPx;
      }
    }, 0);
  }, [isMobile, view, year, month]);

  const handlePrevMonth = useCallback(() => {
    setMonth(m => {
      if (m === 1) {
        setYear(y => y - 1);
        return 12;
      }
      return m - 1;
    });
  }, []);

  const handleNextMonth = useCallback(() => {
    setMonth(m => {
      if (m === 12) {
        setYear(y => y + 1);
        return 1;
      }
      return m + 1;
    });
  }, []);

  const handlePrevYear = useCallback(() => setYear(y => y - 1), []);
  const handleNextYear = useCallback(() => setYear(y => y + 1), []);

  const handleSelectDay = useCallback((d: number, isCurrentMonth: boolean) => {
    if (isCurrentMonth) setDay(d);
  }, []);

  const handleConfirmYearMonth = useCallback(() => {
    setYear(tempYear);
    setMonth(tempMonth);
    setView('calendar');
  }, [tempYear, tempMonth]);

  const handleConfirm = useCallback(() => {
    const safeDay = Math.min(day ?? 1, new Date(year, month, 0).getDate());
    onConfirm(formatDate(year, month, safeDay));
    onCancel();
  }, [year, month, day, onConfirm, onCancel]);

  const calendarDays = useMemo(() => getCalendarDays(year, month, day), [year, month, day]);
  const variant = isMobile ? 'sheet' : 'modal';
  const showMobilePicker = isMobile && view !== 'calendar';

  const scrollToMobileIndex = useCallback((ref: React.RefObject<HTMLDivElement>, index: number) => {
    if (!ref.current || typeof window === 'undefined') return;
    const itemHeightPx = (window.innerWidth * MOBILE_PICKER_ITEM_HEIGHT) / 393;
    ref.current.scrollTo({ top: index * itemHeightPx, behavior: 'smooth' });
  }, []);

  const handleMobileYearScroll = useCallback(() => {
    if (!mobileYearRef.current || typeof window === 'undefined') return;
    const scrollPos = mobileYearRef.current.scrollTop;
    const itemHeightPx = (window.innerWidth * MOBILE_PICKER_ITEM_HEIGHT) / 393;
    const index = Math.round(scrollPos / itemHeightPx);
    if (index >= 0 && index < YEARS.length && YEARS[index] !== tempYear) {
      setTempYear(YEARS[index]);
    }
  }, [tempYear]);

  const handleMobileMonthScroll = useCallback(() => {
    if (!mobileMonthRef.current || typeof window === 'undefined') return;
    const scrollPos = mobileMonthRef.current.scrollTop;
    const itemHeightPx = (window.innerWidth * MOBILE_PICKER_ITEM_HEIGHT) / 393;
    const index = Math.round(scrollPos / itemHeightPx);
    if (index >= 0 && index < MOBILE_MONTHS.length && MOBILE_MONTHS[index] !== tempMonth) {
      setTempMonth(MOBILE_MONTHS[index]);
    }
  }, [tempMonth]);

  if (!isOpen) return null;

  return (
    <Overlay $variant={variant} onClick={(e) => e.target === e.currentTarget && onCancel()}>
      <Modal $variant={variant} onClick={(e) => e.stopPropagation()}>
        {isMobile && (
          <MobileHeader>
            <MobileTitle>생년월일을 선택해주세요</MobileTitle>
            <MobileCloseButton type="button" onClick={onCancel} aria-label="닫기" />
          </MobileHeader>
        )}
        {view === 'calendar' && (
          <>
            {isMobile ? (
              <MobileCalendarHeader>
                <MobileNavGroup>
                  <MobileNavButton type="button" onClick={handlePrevMonth} aria-label="이전 달">
                    <img src={chevronBackIcon} alt="" />
                  </MobileNavButton>
                  <MobileNavLabel type="button" onClick={() => setView('month')}>
                    {year}년 {month}월 <MobileDropdownIcon src={dropdownIcon} alt="" />
                  </MobileNavLabel>
                  <MobileNavButton type="button" $right onClick={handleNextMonth} aria-label="다음 달">
                    <img src={chevronBackIcon} alt="" />
                  </MobileNavButton>
                </MobileNavGroup>
              </MobileCalendarHeader>
            ) : (
              <Header>
                <NavGroup>
                  <NavButton type="button" onClick={handlePrevMonth} aria-label="이전 달">
                    <img src={chevronBackIcon} alt="" />
                  </NavButton>
                  <NavLabel type="button" onClick={() => setView('month')}>
                    {month}월 <DropdownIcon src={dropdownIcon} alt="" />
                  </NavLabel>
                  <NavButton type="button" $right onClick={handleNextMonth} aria-label="다음 달">
                    <img src={chevronBackIcon} alt="" />
                  </NavButton>
                </NavGroup>
                <NavGroup>
                  <NavButton type="button" onClick={handlePrevYear} aria-label="이전 해">
                    <img src={chevronBackIcon} alt="" />
                  </NavButton>
                  <NavLabel type="button" onClick={() => setView('year')}>
                    {year} <DropdownIcon src={dropdownIcon} alt="" />
                  </NavLabel>
                  <NavButton type="button" $right onClick={handleNextYear} aria-label="다음 해">
                    <img src={chevronBackIcon} alt="" />
                  </NavButton>
                </NavGroup>
              </Header>
            )}
            <CalendarContent>
              <WeekdayRow>
                {WEEKDAYS.map((w) => (
                  <WeekdayCell key={w}>{w}</WeekdayCell>
                ))}
              </WeekdayRow>
              <CalendarGrid>
                {calendarDays.map((cell, i) => (
                  <DayCell
                    key={i}
                    type="button"
                    $dimmed={!cell.isCurrentMonth}
                    $selected={cell.isSelected}
                    onClick={() => handleSelectDay(cell.date, cell.isCurrentMonth)}
                  >
                    {cell.date}
                  </DayCell>
                ))}
              </CalendarGrid>
            </CalendarContent>
            {isMobile ? (
              <MobileConfirmButton type="button" onClick={handleConfirm}>
                선택 완료
              </MobileConfirmButton>
            ) : (
              <Footer>
                <FooterBtn type="button" onClick={onCancel}>
                  취소
                </FooterBtn>
                <FooterBtn type="button" onClick={handleConfirm}>
                  확인
                </FooterBtn>
              </Footer>
            )}
          </>
        )}

        {showMobilePicker && (
          <>
            <MobilePickerContent>
              <MobilePickerScrollArea>
                <MobilePickerColumn ref={mobileYearRef} onScroll={handleMobileYearScroll}>
                  {YEARS.map((y, index) => {
                    const isSelected = tempYear === y;
                    return (
                      <MobilePickerItem
                        key={y}
                        $selected={isSelected}
                        onClick={() => {
                          setTempYear(y);
                          scrollToMobileIndex(mobileYearRef, index);
                        }}
                      >
                        {y}년
                      </MobilePickerItem>
                    );
                  })}
                </MobilePickerColumn>
                <MobilePickerColumn ref={mobileMonthRef} onScroll={handleMobileMonthScroll}>
                  {MOBILE_MONTHS.map((m, index) => {
                    const isSelected = tempMonth === m;
                    return (
                      <MobilePickerItem
                        key={m}
                        $selected={isSelected}
                        onClick={() => {
                          setTempMonth(m);
                          scrollToMobileIndex(mobileMonthRef, index);
                        }}
                      >
                        {MONTHS[m - 1]}
                      </MobilePickerItem>
                    );
                  })}
                </MobilePickerColumn>
              </MobilePickerScrollArea>
            </MobilePickerContent>
            <MobileConfirmButton type="button" onClick={handleConfirmYearMonth}>
              선택 완료
            </MobileConfirmButton>
          </>
        )}

        {!isMobile && view === 'month' && (
          <DropdownWrap>
            <DropdownHeader>
              <DropdownLabelGroup>
                {month}월 <DropdownIcon src={dropdownIcon} alt="" />
              </DropdownLabelGroup>
              <DropdownHeaderPart type="button" onClick={() => setView('year')}>
                {year}
              </DropdownHeaderPart>
            </DropdownHeader>
            <DropdownList ref={monthListRef}>
              {MONTHS.map((m, i) => {
                const monthNum = i + 1;
                const isSelected = month === monthNum;
                return (
                  <DropdownItem
                    key={m}
                    data-month={monthNum}
                    $selected={isSelected}
                    onClick={() => {
                      setMonth(monthNum);
                      setView('calendar');
                    }}
                  >
                    <CheckSlot>{isSelected && <CheckIcon>✓</CheckIcon>}</CheckSlot>
                    {m}
                  </DropdownItem>
                );
              })}
            </DropdownList>
          </DropdownWrap>
        )}

        {!isMobile && view === 'year' && (
          <DropdownWrap>
            <DropdownHeader>
              <DropdownHeaderPart type="button" onClick={() => setView('month')}>
                {month}월
              </DropdownHeaderPart>
              <DropdownLabelGroup>
                {year} <DropdownIcon src={dropdownIcon} alt="" />
              </DropdownLabelGroup>
            </DropdownHeader>
            <DropdownList ref={yearListRef}>
              {YEARS.map((y) => {
                const isSelected = year === y;
                return (
                  <DropdownItem
                    key={y}
                    data-year={y}
                    $selected={isSelected}
                    onClick={() => {
                      setYear(y);
                      setView('calendar');
                    }}
                  >
                    <CheckSlot>{isSelected && <CheckIcon>✓</CheckIcon>}</CheckSlot>
                    {y}
                  </DropdownItem>
                );
              })}
            </DropdownList>
          </DropdownWrap>
        )}
      </Modal>
    </Overlay>
  );
}
